import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  PlusCircle,
  Edit,
  Trash2,
  DollarSign,
  RefreshCcw,
  XCircle,
  History,
  Info,
  CreditCard,
  Banknote,
  Repeat,
  ShoppingCart,
  LogOut,
} from "lucide-react";

// Configuración de la conexión al backend (¡ESTO DEBE APUNTAR A TU BACKEND REAL!)
const API_BASE_URL = "https://supersofia.nvirred.com/consulta.php"; // ¡CAMBIA ESTO POR LA URL DE TU BACKEND!

const AdminPage = () => {
  const [paymentPoints, setPaymentPoints] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]); // Nuevo estado para métodos de pago
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPointForDetails, setSelectedPointForDetails] = useState(null);
  const [currentPointSales, setCurrentPointSales] = useState(null);
  const [cashClosureHistory, setCashClosureHistory] = useState([]);
  const [newMethodName, setNewMethodName] = useState("");
  const [editingMethodId, setEditingMethodId] = useState(null);
  const [editingMethodName, setEditingMethodName] = useState("");
  const navigate = useNavigate();

  // Simulación de autenticación (esto debería venir del App.js o un contexto de autenticación)
  const [isLoggedIn, setIsLoggedIn] = useState(true); // Asumimos logueado para esta página

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login");
    }
  }, [isLoggedIn, navigate]);

  // Función para obtener los puntos de venta desde el backend
  const fetchPaymentPoints = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}?route=payment-points`);
      if (!response.ok) throw new Error("Error al cargar puntos de venta");
      const data = await response.json();
      setPaymentPoints(data);
      setError(null);
    } catch (err) {
      setError(
        "Error al cargar los puntos de venta. Asegúrate de que el backend esté funcionando."
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener los métodos de pago desde el backend
  const fetchPaymentMethods = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}?route=payment-methods`);
      if (!response.ok) throw new Error("Error al cargar métodos de pago");
      const data = await response.json();
      setPaymentMethods(data);
    } catch (err) {
      console.error("Error al cargar los métodos de pago:", err);
    }
  };

  useEffect(() => {
    fetchPaymentPoints();
    fetchPaymentMethods();
  }, []);

  // Función para obtener las ventas actuales de un punto de venta
  const fetchCurrentPointSales = async (pointId) => {
    try {
      console.log("🔍 Fetching current sales for point:", pointId);
      const response = await fetch(
        `${API_BASE_URL}?route=payment-points/${pointId}/current-sales`
      );
      if (!response.ok) throw new Error("Error al cargar ventas actuales");
      const data = await response.json();
      
      console.log("📦 Current sales data:", data);
      
      // Asegurar que todos los valores sean números
      const sanitizedData = {
        total: parseFloat(data.total) || 0,
        mercadopago: parseFloat(data.mercadopago) || 0,
        transferencia: parseFloat(data.transferencia) || 0,
        tarjeta_credito: parseFloat(data.tarjeta_credito) || 0,
        efectivo: parseFloat(data.efectivo) || 0,
      };
      
      console.log("✅ Sanitized sales data:", sanitizedData);
      setCurrentPointSales(sanitizedData);
    } catch (err) {
      console.error("Error al cargar ventas actuales:", err);
      setCurrentPointSales({
        total: 0,
        mercadopago: 0,
        transferencia: 0,
        tarjeta_credito: 0,
        efectivo: 0,
      }); // Reset en caso de error
    }
  };

  // Función para obtener el historial de cierres de caja
  const fetchCashClosureHistory = async (pointId) => {
    try {
      console.log("🔍 Fetching cash closure history for point:", pointId);
      const response = await fetch(
        `${API_BASE_URL}?route=cash-closure-history&id=${pointId}`
      );
      if (!response.ok) throw new Error("Error al cargar historial de cierres");
      const data = await response.json();
      
      console.log("📦 Raw closure history data:", data);
      
      // Sanitizar los datos del historial
      const sanitizedHistory = data.map(closure => ({
        ...closure,
        total: parseFloat(closure.total) || 0,
        details: typeof closure.details === 'string' 
          ? JSON.parse(closure.details || '{}') 
          : (closure.details || {})
      }));
      
      console.log("✅ Sanitized closure history:", sanitizedHistory);
      setCashClosureHistory(sanitizedHistory);
    } catch (err) {
      console.error("Error al cargar historial de cierres:", err);
      setCashClosureHistory([]); // Reset en caso de error
    }
  };

  const handleDeletePoint = async (id) => {
    if (
      window.confirm(
        "¿Estás seguro de que quieres eliminar este punto de venta? Esto es irreversible."
      )
    ) {
      try {
        const response = await fetch(
          `${API_BASE_URL}?route=payment-points/${id}`,
          {
            method: "DELETE",
          }
        );
        if (!response.ok) throw new Error("Error al eliminar punto de venta");
        setPaymentPoints((prev) => prev.filter((point) => point.id !== id));
        alert("Punto de venta eliminado con éxito.");
      } catch (err) {
        alert("Error al eliminar el punto de venta.");
        console.error(err);
      }
    }
  };

  const handleCloseCash = async (pointId) => {
    if (
      window.confirm(
        "¿Estás seguro de que quieres realizar el cierre de caja para este punto? Esto borrará los cobros actuales y los registrará en el historial."
      )
    ) {
      try {
        const response = await fetch(
          `${API_BASE_URL}?route=cash-closure&id=${pointId}`,
          { method: "POST" }
        );
        if (!response.ok)
          throw new Error("Error al realizar el cierre de caja");
        alert(
          `Cierre de caja realizado para ${
            paymentPoints.find((p) => p.id === pointId)?.name
          }. Cobros borrados.`
        );

        setCurrentPointSales(null); // Limpiar ventas actuales
        await fetchCashClosureHistory(pointId); // Recargar historial para el punto actual
      } catch (err) {
        alert("Error al realizar el cierre de caja.");
        console.error(err);
      }
    }
  };

  const handleAddMethod = async (e) => {
    e.preventDefault();
    if (!newMethodName.trim()) return;
    try {
      const response = await fetch(`${API_BASE_URL}?route=payment-methods`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newMethodName.trim().toLowerCase().replace(/\s/g, "_"),
          display_name: newMethodName.trim(),
        }),
      });
      if (!response.ok) throw new Error("Error al agregar método de pago");
      alert("Método de pago agregado con éxito.");
      setNewMethodName("");
      fetchPaymentMethods(); // Recargar métodos
    } catch (err) {
      alert("Error al agregar método de pago.");
      console.error(err);
    }
  };

  const handleEditMethod = async (id) => {
    if (!editingMethodName.trim()) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}?route=payment-methods/${id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ display_name: editingMethodName.trim() }),
        }
      );
      if (!response.ok) throw new Error("Error al editar método de pago");
      alert("Método de pago editado con éxito.");
      setEditingMethodId(null);
      setEditingMethodName("");
      fetchPaymentMethods(); // Recargar métodos
    } catch (err) {
      alert("Error al editar método de pago.");
      console.error(err);
    }
  };

  const handleDeleteMethod = async (id) => {
    if (
      window.confirm(
        "¿Estás seguro de que quieres eliminar este método de pago?"
      )
    ) {
      try {
        const response = await fetch(
          `${API_BASE_URL}?route=payment-methods/${id}`,
          {
            method: "DELETE",
          }
        );
        if (!response.ok) throw new Error("Error al eliminar método de pago");
        alert("Método de pago eliminado con éxito.");
        fetchPaymentMethods(); // Recargar métodos
      } catch (err) {
        alert("Error al eliminar método de pago.");
        console.error(err);
      }
    }
  };

  const getMethodIcon = (methodName) => {
    switch (methodName) {
      case "mercadopago":
        return <ShoppingCart className="w-5 h-5 text-blue-500" />;
      case "transferencia":
        return <Repeat className="w-5 h-5 text-purple-500" />;
      case "tarjeta_credito":
        return <CreditCard className="w-5 h-5 text-orange-500" />;
      case "efectivo":
        return <Banknote className="w-5 h-5 text-green-500" />;
      default:
        return <DollarSign className="w-5 h-5 text-gray-500" />;
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false); // Simular logout
    navigate("/login");
  };

  // Función helper para formatear números de manera segura
  const safeToFixed = (value, decimals = 2) => {
    const num = parseFloat(value);
    return isNaN(num) ? "0.00" : num.toFixed(decimals);
  };

  if (!isLoggedIn) {
    return null; // O un spinner, o un mensaje de "redirigiendo"
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <p className="text-gray-600 text-xl">
          Cargando... ¡No te impacientes, que la información es poder!
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8">
        <XCircle className="w-16 h-16 text-red-500 mb-4" />
        <p className="text-red-600 text-xl text-center">{error}</p>
        <p className="text-gray-500 mt-2">
          Revisa la URL de tu backend en `API_BASE_URL` y que esté funcionando
          correctamente.
        </p>
      </div>
    );
  }

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <div className="container mx-auto max-w-4xl">
        <div className="flex justify-between items-center mb-8">
          <motion.h1
            className="text-4xl font-bold text-gray-900"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            Panel de Administración
          </motion.h1>
          <motion.button
            onClick={handleLogout}
            className="bg-red-100 text-red-700 font-semibold py-2 px-4 rounded-full shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center gap-2"
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <LogOut className="w-5 h-5" />
            Cerrar Sesión
          </motion.button>
        </div>

        {/* Sección de Puntos de Venta */}
        <motion.div
          className="bg-white rounded-3xl shadow-xl p-6 mb-8 border border-gray-200 flex justify-between items-center"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <h2 className="text-2xl font-semibold text-gray-800">
            Gestión de Puntos de Venta
          </h2>
          <Link
            to="/create-point"
            className="bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold py-3 px-6 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2"
          >
            <PlusCircle className="w-5 h-5" />
            Nuevo Punto
          </Link>
        </motion.div>

        {paymentPoints.length === 0 ? (
          <motion.div
            className="bg-white rounded-3xl shadow-xl p-12 text-center border border-gray-200"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <p className="text-gray-600 text-lg">
              Aún no tienes puntos de venta. ¡Es hora de crear tu primer
              imperio!
            </p>
            <Link
              to="/create-point"
              className="mt-6 inline-block bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold py-3 px-8 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              Crear Ahora
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-6 mb-8">
            {paymentPoints.map((point, index) => (
              <motion.div
                key={point.id}
                className="bg-white rounded-3xl shadow-lg p-6 border border-gray-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1 * index + 0.6, duration: 0.5 }}
              >
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {point.name}
                  </h3>
                  <p className="text-gray-600 text-sm">{point.description}</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to={`/point/${point.id}`}
                    className="bg-blue-100 text-blue-700 font-medium py-2 px-4 rounded-full flex items-center gap-2 hover:bg-blue-200 transition-colors duration-300"
                  >
                    <DollarSign className="w-4 h-4" />
                    Ir a Cobrar
                  </Link>
                  <button
                    onClick={() => {
                      setSelectedPointForDetails(point.id);
                      fetchCurrentPointSales(point.id);
                      fetchCashClosureHistory(point.id); // Cargar historial al ver detalles
                    }}
                    className="bg-indigo-100 text-indigo-700 font-medium py-2 px-4 rounded-full flex items-center gap-2 hover:bg-indigo-200 transition-colors duration-300"
                  >
                    <Info className="w-4 h-4" />
                    Ver Detalles
                  </button>
                  <button
                    onClick={() => handleDeletePoint(point.id)}
                    className="bg-red-100 text-red-700 font-medium py-2 px-4 rounded-full flex items-center gap-2 hover:bg-red-200 transition-colors duration-300"
                  >
                    <Trash2 className="w-4 h-4" />
                    Eliminar
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Sección de Gestión de Métodos de Cobro */}
        <motion.div
          className="bg-white rounded-3xl shadow-xl p-6 mb-8 border border-gray-200"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">
            Gestión de Métodos de Cobro
          </h2>
          <form onSubmit={handleAddMethod} className="flex gap-3 mb-6">
            <input
              type="text"
              value={newMethodName}
              onChange={(e) => setNewMethodName(e.target.value)}
              placeholder="Nuevo método de cobro (ej: Bitcoin)"
              className="flex-1 px-4 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="submit"
              className="bg-blue-500 text-white font-semibold py-2 px-5 rounded-xl hover:bg-blue-600 transition-colors duration-300"
            >
              Agregar
            </button>
          </form>

          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <motion.div
                key={method.id}
                className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex items-center justify-between"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.05 }}
              >
                <div className="flex items-center gap-3">
                  {getMethodIcon(method.name)}
                  {editingMethodId === method.id ? (
                    <input
                      type="text"
                      value={editingMethodName}
                      onChange={(e) => setEditingMethodName(e.target.value)}
                      className="px-2 py-1 rounded border border-gray-300"
                    />
                  ) : (
                    <span className="font-medium text-gray-800">
                      {method.display_name}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {editingMethodId === method.id ? (
                    <button
                      onClick={() => handleEditMethod(method.id)}
                      className="bg-green-100 text-green-700 py-1 px-3 rounded-full text-sm hover:bg-green-200"
                    >
                      Guardar
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setEditingMethodId(method.id);
                        setEditingMethodName(method.display_name);
                      }}
                      className="bg-yellow-100 text-yellow-700 py-1 px-3 rounded-full text-sm hover:bg-yellow-200"
                    >
                      Editar
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteMethod(method.id)}
                    className="bg-red-100 text-red-700 py-1 px-3 rounded-full text-sm hover:bg-red-200"
                  >
                    Eliminar
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Modal de Detalles y Cierre de Caja */}
        <AnimatePresence>
          {selectedPointForDetails && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-2xl relative border border-gray-200"
                initial={{ scale: 0.9, y: 50 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 50 }}
                transition={{ type: "spring", stiffness: 100, damping: 15 }}
              >
                <button
                  onClick={() => setSelectedPointForDetails(null)}
                  className="absolute top-4 right-4 bg-gray-100 rounded-full p-2 text-gray-600 hover:bg-gray-200 transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  Detalles y Cierre de Caja:{" "}
                  {
                    paymentPoints.find((p) => p.id === selectedPointForDetails)
                      ?.name
                  }
                </h2>

                {currentPointSales ? (
                  <div className="mb-8 space-y-4">
                    <h3 className="text-2xl font-semibold text-gray-800 mb-3">
                      Ventas Actuales
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                        <p className="text-blue-700 font-medium">
                          Total Vendido:
                        </p>
                        <p className="text-3xl font-bold text-blue-900">
                          ${currentPointSales.total.toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                        <p className="text-green-700 font-medium">Efectivo:</p>
                        <p className="text-2xl font-bold text-green-900">
                          ${currentPointSales.efectivo.toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                        <p className="text-purple-700 font-medium">
                          Mercado Pago:
                        </p>
                        <p className="text-2xl font-bold text-purple-900">
                          ${currentPointSales.mercadopago.toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
                        <p className="text-orange-700 font-medium">
                          Tarjeta Crédito:
                        </p>
                        <p className="text-2xl font-bold text-orange-900">
                          ${currentPointSales.tarjeta_credito.toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                        <p className="text-yellow-700 font-medium">
                          Transferencia:
                        </p>
                        <p className="text-2xl font-bold text-yellow-900">
                          ${currentPointSales.transferencia.toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCloseCash(selectedPointForDetails)}
                      className="w-full bg-gradient-to-r from-green-500 to-teal-600 text-white font-semibold py-3 px-6 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 mt-6"
                    >
                      <RefreshCcw className="w-5 h-5" />
                      Realizar Cierre de Caja
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-600 text-center py-4">
                    Cargando ventas actuales o no hay ventas registradas.
                  </p>
                )}

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <History className="w-6 h-6 text-gray-600" />
                    Historial de Cierres de Caja
                  </h3>
                  {cashClosureHistory.length === 0 ? (
                    <p className="text-gray-600 text-center py-4">
                      No hay historial de cierres para este punto.
                    </p>
                  ) : (
                    <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                      {cashClosureHistory.map((closure) => (
                        <div
                          key={closure.id}
                          className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex justify-between items-center"
                        >
                          <div>
                            <p className="font-medium text-gray-800">
                              Cierre del{" "}
                              {new Date(closure.closeTime).toLocaleString()}
                            </p>
                            <p className="text-sm text-gray-500">
                              Total: ${closure.total.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default AdminPage;
